"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

type AnimationVariant = "fade-up" | "fade-in" | "slide-left" | "slide-right" | "scale" | "blur-up";

interface AnimateOnScrollProps {
  children: ReactNode;
  variant?: AnimationVariant;
  delay?: number;
  duration?: number;
  threshold?: number;
  once?: boolean;
  className?: string;
}

export function AnimateOnScroll({
  children,
  variant = "fade-up",
  delay = 0,
  duration = 700,
  threshold = 0.15,
  once = true,
  className = "",
}: AnimateOnScrollProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    // Check if element is already visible on mount
    const rect = element.getBoundingClientRect();
    const isAlreadyVisible = rect.top < window.innerHeight - 80 && rect.bottom > 0;

    if (isAlreadyVisible) {
      // Small delay to allow initial render, then trigger animation
      const timeoutId = setTimeout(() => setIsVisible(true), 50);
      if (once) return () => clearTimeout(timeoutId);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(element);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: "0px 0px -80px 0px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, once]);

  const baseStyles: React.CSSProperties = {
    transitionProperty: "opacity, transform, filter",
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
    transitionDelay: `${delay}ms`,
    willChange: isVisible ? "auto" : "opacity, transform",
  };

  const variantStyles: Record<AnimationVariant, { initial: React.CSSProperties; visible: React.CSSProperties }> = {
    "fade-up": {
      initial: { opacity: 0, transform: "translateY(20px)" },
      visible: { opacity: 1, transform: "translateY(0)" },
    },
    "fade-in": {
      initial: { opacity: 0 },
      visible: { opacity: 1 },
    },
    "slide-left": {
      initial: { opacity: 0, transform: "translateX(24px)" },
      visible: { opacity: 1, transform: "translateX(0)" },
    },
    "slide-right": {
      initial: { opacity: 0, transform: "translateX(-24px)" },
      visible: { opacity: 1, transform: "translateX(0)" },
    },
    "scale": {
      initial: { opacity: 0, transform: "scale(0.96)" },
      visible: { opacity: 1, transform: "scale(1)" },
    },
    "blur-up": {
      initial: { opacity: 0, transform: "translateY(16px)", filter: "blur(4px)" },
      visible: { opacity: 1, transform: "translateY(0)", filter: "blur(0px)" },
    },
  };

  const currentVariant = variantStyles[variant];
  const animationStyles = isVisible ? currentVariant.visible : currentVariant.initial;

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...baseStyles, ...animationStyles }}
    >
      {children}
    </div>
  );
}
